import {
  Select as AriaSelect,
  SelectValue,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  type SelectProps as AriaSelectProps,
  type ListBoxItemProps,
} from "react-aria-components";
import { ChevronDown } from "lucide-react";
import { tv } from "tailwind-variants";
import { focusRing } from "../utils/focusRing";
import { composeTailwindRenderProps } from "../utils/utils";

const trigger = tv({
  extend: focusRing,
  base: "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
  variants: {
    isInvalid: {
      true: "border-destructive",
    },
  },
});

const popover = tv({
  base: "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2",
});

const listBox = tv({
  base: "outline-none",
});

const listBoxItem = tv({
  extend: focusRing,
  base: "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[focused]:bg-accent data-[focused]:text-accent-foreground data-[disabled]:opacity-50",
});

export interface SelectProps<T extends object> extends Omit<AriaSelectProps<T>, "children"> {
  label?: string;
  description?: string;
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  placeholder?: string;
}

export function Select<T extends object>({
  label,
  description,
  children,
  placeholder,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect
      {...props}
      className={composeTailwindRenderProps(props.className, "group flex flex-col gap-1")}
    >
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </label>
      )}
      <Button className={trigger}>
        <SelectValue className="placeholder:text-muted-foreground">
          {({ selectedText }) => selectedText || placeholder}
        </SelectValue>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>
      {description && <div className="text-muted-foreground text-sm">{description}</div>}
      <Popover className={popover}>
        <ListBox className={listBox}>{children}</ListBox>
      </Popover>
    </AriaSelect>
  );
}

export function SelectItem({ children, ...props }: ListBoxItemProps) {
  return (
    <ListBoxItem {...props} className={listBoxItem}>
      {children}
    </ListBoxItem>
  );
}
